import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import RecommendationsCard from '../Components/RecommendationsCard';
import { GetSingleData } from '../NetworkCalls/Products/ServerReq';
import Navbar from '../Components/Navbar';
import { Container, Typography, IconButton, Box, CircularProgress, Card, CardContent, Button, Stack, useMediaQuery } from '@mui/material';
import DeleteModal from '../Components/modals/DeleteModal';
import { axiosInstance } from '../axios';

const Recommendations = () => {
    const { bossId } = useParams();
    const navigate = useNavigate();
    const [recommendations, setRecommendations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState('');
    const [deletingId, setDeletingId] = useState(null);
    const isMobile = useMediaQuery('(max-width:600px)');

    const fetchRecommendations = async () => {
        setLoading(true);
        try {
            console.log('Fetching recommendations for bossId:', bossId);
            const response = await GetSingleData(`recommendation/bossRecommendation/${bossId}`);
            console.log('API Response:', response);

            // Check if response is an array
            if (Array.isArray(response)) {
                setRecommendations(response);
                console.log('Set recommendations:', response);
            } else if (response?.data) {
                // Fallback for response.data structure
                setRecommendations(response.data);
                console.log('Set recommendations:', response.data);
            } else {
                console.log('No valid data in response');
                setRecommendations([]);
            }
        } catch (error) {
            console.error('Error fetching recommendations:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (bossId) {
            fetchRecommendations();
        }
    }, [bossId]);

    const handleLogout = () => {
        // Add your logout logic here
        console.log('Logout clicked from Navbar');
        // Example: navigate('/login');
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        setError('');
        try {
            const response = await axiosInstance.delete(
                `/recommendation/deleteRecommendation/${deletingId}`
            );

            if (response.data.success) {
                setOpenDeleteModal(false);
                fetchRecommendations(); // Reload data
            } else {
                setError(response.data.message || 'Delete failed');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Server error');
        } finally {
            setIsDeleting(false);
        }
    };

    const formatTimeAgo = (date) => {
        const now = new Date();
        const diffInMinutes = Math.floor((now - new Date(date)) / (1000 * 60));

        if (diffInMinutes < 60) {
            return `${diffInMinutes} min ago`;
        }

        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) {
            return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
        }

        const diffInDays = Math.floor(diffInHours / 24);
        return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    };

    const formatStrengths = (strengthsText) => {
        if (!strengthsText) return [];
        return strengthsText.split('\n').filter(strength => strength.trim());
    };

    return (
        <Box sx={{ backgroundColor: '#F5F6FE', minHeight: '100vh' }}>
            <Navbar title="Recommendations" onLogout={handleLogout} />
            <Box sx={{ maxWidth: '800px', mx: 'auto', p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'left', alignItems: 'left', gap: '16px', mb: 2 }}>
                    <IconButton onClick={() => navigate(-1)}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="20" viewBox="0 0 16 28" fill="none">
                            <path d="M14 2L2 14L14 26" stroke="black" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </IconButton>
                    <Typography variant="h6" sx={{ fontWeight: '600', color: '#000000', fontFamily: 'Urbanist', fontSize: '26px' }}>
                        Recommendations
                    </Typography>
                </Box>

                {loading ? (
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        py={8}
                        gap={2}
                    >
                        <CircularProgress size={40} sx={{ color: '#12188A' }} />
                    </Box>
                ) : recommendations.length > 0 ? (
                    // Single unified container for all recommendations
                    <Card sx={{
                        borderRadius: '12px',
                        boxShadow: '-2px 2px 12px 0px rgba(0, 0, 0, 0.12)',
                        overflow: 'visible'
                    }}>
                        <CardContent sx={{
                            p: { xs: 2, sm: 2.5 },
                            '&:last-child': { pb: { xs: 2, sm: 2.5 } }
                        }}>
                            {recommendations.map((recommendation, index) => (
                                <Box key={recommendation._id}>
                                    {/* Header with name, time, and buttons */}
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        justifyContent: 'space-between',
                                        alignItems: { xs: 'flex-start', sm: 'center' },
                                        mb: 1.5,
                                        gap: { xs: 1.5, sm: 0 },

                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 500,
                                                    color: '#1a1a1a',
                                                    fontSize: { xs: '15px', sm: '16px', lg: '18px' },
                                                    fontFamily: 'Urbanist'
                                                }}
                                            >
                                                {recommendation.userName}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: '#888888',
                                                    fontSize: { xs: '12px', sm: '13px' },
                                                    fontFamily: 'Urbanist'
                                                }}
                                            >
                                                {formatTimeAgo(recommendation.createdAt)}
                                            </Typography>
                                        </Box>
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            spacing={1}
                                            width={{ xs: '100%', sm: 'auto' }}
                                        >
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                fullWidth={isMobile}
                                                sx={{
                                                    minWidth: 'auto',
                                                    px: 2,
                                                    py: 0.5,
                                                    borderColor: '#000000',
                                                    color: '#000000',
                                                    borderRadius: '52px',
                                                    fontSize: { xs: '11px', sm: '12px' },
                                                    fontWeight: 400,
                                                    textTransform: 'none',
                                                    fontFamily: 'Urbanist',
                                                    '&:hover': {
                                                        borderColor: '#000000',
                                                        backgroundColor: '#f9f9f9'
                                                    }
                                                }}
                                                onClick={() => {
                                                    setDeletingId(recommendation._id);
                                                    setOpenDeleteModal(true);
                                                }}
                                            >
                                                <span style={{ marginRight: '8px' }}>Delete</span>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="0 0 20 22" fill="none">
                                                    <path d="M8.26562 8.375C8.26562 8.27554 8.22612 8.18016 8.15579 8.10983C8.08546 8.03951 7.99008 8 7.89062 8C7.79117 8 7.69579 8.03951 7.62546 8.10983C7.55513 8.18016 7.51562 8.27554 7.51562 8.375V16.625C7.51562 16.7245 7.55513 16.8198 7.62546 16.8902C7.69579 16.9605 7.79117 17 7.89062 17C7.99008 17 8.08546 16.9605 8.15579 16.8902C8.22612 16.8198 8.26562 16.7245 8.26562 16.625V8.375ZM11.6406 8C11.7401 8 11.8355 8.03951 11.9058 8.10983C11.9761 8.18016 12.0156 8.27554 12.0156 8.375V16.625C12.0156 16.7245 11.9761 16.8198 11.9058 16.8902C11.8355 16.9605 11.7401 17 11.6406 17C11.5412 17 11.4458 16.9605 11.3755 16.8902C11.3051 16.8198 11.2656 16.7245 11.2656 16.625V8.375C11.2656 8.27554 11.3051 8.18016 11.3755 8.10983C11.4458 8.03951 11.5412 8 11.6406 8ZM13.1406 3.875V4.25H19.1406C19.2401 4.25 19.3355 4.28951 19.4058 4.35984C19.4761 4.43016 19.5156 4.52554 19.5156 4.625C19.5156 4.72446 19.4761 4.81984 19.4058 4.89016C19.3355 4.96049 19.2401 5 19.1406 5H17.2334L16.0124 18.4303C15.9362 19.2691 15.5491 20.0492 14.9271 20.6172C14.3051 21.1852 13.4932 21.5001 12.6509 21.5H6.88037C6.03806 21.5001 5.22615 21.1852 4.60416 20.6172C3.98217 20.0492 3.59506 19.2691 3.51888 18.4303L2.29788 5H0.390625C0.291169 5 0.195786 4.96049 0.12546 4.89016C0.0551339 4.81984 0.015625 4.72446 0.015625 4.625C0.015625 4.52554 0.0551339 4.43016 0.12546 4.35984C0.195786 4.28951 0.291169 4.25 0.390625 4.25H6.39062V3.875C6.39062 2.97989 6.7462 2.12145 7.37914 1.48851C8.01207 0.855579 8.87052 0.5 9.76562 0.5C10.6607 0.5 11.5192 0.855579 12.1521 1.48851C12.785 2.12145 13.1406 2.97989 13.1406 3.875ZM7.14062 3.875V4.25H12.3906V3.875C12.3906 3.17881 12.1141 2.51113 11.6218 2.01884C11.1295 1.52656 10.4618 1.25 9.76562 1.25C9.06943 1.25 8.40175 1.52656 7.90947 2.01884C7.41719 2.51113 7.14062 3.17881 7.14062 3.875ZM3.05162 5L4.26663 18.3627C4.32593 19.0149 4.62684 19.6213 5.11029 20.063C5.59373 20.5047 6.2248 20.7497 6.87963 20.75H12.6501C13.3052 20.7501 13.9366 20.5052 14.4204 20.0635C14.9042 19.6218 15.2053 19.0151 15.2646 18.3627L16.4804 5H3.05162Z" fill="black" />
                                                </svg>
                                            </Button>

                                            <Button
                                                variant="contained"
                                                size="small"
                                                fullWidth={isMobile}
                                                onClick={() => window.open(recommendation.profileUrl, '_blank')}
                                                sx={{
                                                    px: 2,
                                                    py: 0.5,
                                                    borderRadius: '20px',
                                                    textTransform: 'none',
                                                    border: '1px solid #000000',
                                                    backgroundColor: '#ffffff',
                                                    fontSize: { xs: '11px', sm: '12px' },
                                                    fontWeight: 400,
                                                    fontFamily: 'Urbanist',
                                                    color: '#000000',
                                                    boxShadow: 'none',
                                                    '&:hover': {
                                                        backgroundColor: '#f9f9f9',
                                                        boxShadow: 'none'
                                                    }
                                                }}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="22" viewBox="0 0 27 26" fill="none">
                                                    <path d="M21.3467 3.25C21.9213 3.25 22.4723 3.47824 22.8786 3.88452C23.2849 4.2908 23.5131 4.84183 23.5131 5.41639V20.5811C23.5131 21.1557 23.2849 21.7067 22.8786 22.113C22.4723 22.5192 21.9213 22.7475 21.3467 22.7475H6.18201C5.60745 22.7475 5.05642 22.5192 4.65015 22.113C4.24387 21.7067 4.01563 21.1557 4.01562 20.5811V5.41639C4.01562 4.84183 4.24387 4.2908 4.65015 3.88452C5.05642 3.47824 5.60745 3.25 6.18201 3.25H21.3467ZM20.8051 20.0395V14.2986C20.8051 13.362 20.4331 12.4639 19.7709 11.8016C19.1086 11.1394 18.2105 10.7674 17.2739 10.7674C16.3532 10.7674 15.2808 11.3306 14.7609 12.1755V10.9732H11.7388V20.0395H14.7609V14.6994C14.7609 13.8653 15.4325 13.1829 16.2665 13.1829C16.6687 13.1829 17.0545 13.3427 17.3389 13.6271C17.6232 13.9114 17.783 14.2972 17.783 14.6994V20.0395H20.8051ZM8.21842 9.27256C8.70105 9.27256 9.16391 9.08083 9.50519 8.73956C9.84646 8.39829 10.0382 7.93542 10.0382 7.45279C10.0382 6.44542 9.22579 5.62219 8.21842 5.62219C7.73291 5.62219 7.26729 5.81506 6.92399 6.15836C6.58069 6.50167 6.38782 6.96729 6.38782 7.45279C6.38782 8.46016 7.21105 9.27256 8.21842 9.27256ZM9.72406 20.0395V10.9732H6.72361V20.0395H9.72406Z" fill="#2B73EF" />
                                                </svg>
                                                LinkedIn
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 22 22" fill="none">
                                                    <path d="M16.8051 14.9837C16.6662 14.9835 16.5331 14.9281 16.4351 14.8297C16.337 14.7314 16.282 14.5982 16.2821 14.4593L16.2926 6.8318L6.3039 16.7916C6.20549 16.8897 6.07212 16.9447 5.93314 16.9445C5.79416 16.9443 5.66096 16.8889 5.56283 16.7905C5.4647 16.6921 5.40968 16.5587 5.40988 16.4198C5.41008 16.2808 5.46548 16.1476 5.5639 16.0494L15.5526 6.08966L7.92507 6.078C7.78938 6.07301 7.66093 6.01556 7.56677 5.91773C7.47261 5.81991 7.42009 5.68935 7.42029 5.55358C7.42049 5.4178 7.47338 5.2874 7.56782 5.18985C7.66226 5.09229 7.79088 5.03521 7.92658 5.03062L16.8195 5.04349C16.9583 5.04377 17.0914 5.09917 17.1894 5.1975C17.2875 5.29584 17.3425 5.42907 17.3424 5.56794L17.3295 14.4608C17.3292 14.5997 17.2738 14.7328 17.1755 14.8308C17.0772 14.9289 16.9439 14.9839 16.8051 14.9837Z" fill="#181C23" />
                                                </svg>
                                            </Button>
                                        </Stack>
                                    </Box>

                                    {/* Nominator's Perspective with star icon */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 34 34" fill="none">
                                            <path d="M19.4461 4.88186L21.9392 9.90918C22.2792 10.609 23.1857 11.2803 23.9507 11.4088L28.4693 12.1657C31.359 12.6514 32.039 14.7651 29.9567 16.8503L26.4438 20.3923C25.8488 20.9921 25.5231 22.1489 25.7071 22.9774L26.7129 27.362C27.5061 30.8325 25.6788 32.1751 22.6333 30.3612L18.3979 27.8333C17.6331 27.3763 16.3724 27.3763 15.5932 27.8333L11.3579 30.3612C8.32657 32.1751 6.48512 30.8182 7.27837 27.362L8.28409 22.9774C8.46822 22.1489 8.14243 20.9921 7.54749 20.3923L4.03455 16.8503C1.96645 14.7651 2.63221 12.6514 5.52188 12.1657L10.0406 11.4088C10.7913 11.2803 11.6979 10.609 12.0378 9.90918L14.5309 4.88186C15.8907 2.15396 18.1004 2.15396 19.4461 4.88186Z" stroke="black" stroke-width="2.125" stroke-linecap="round" stroke-linejoin="round" />
                                        </svg>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#000000',
                                                fontSize: { xs: '14px', sm: '15px' },
                                                fontFamily: 'Urbanist',
                                                fontWeight: 500
                                            }}
                                        >
                                            Nominator's Perspective
                                        </Typography>
                                    </Box>

                                    {/* Content Sections with compact spacing */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                                        <Box>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: '#1a1a1a',
                                                    mb: 0.25,
                                                    fontSize: { xs: '12px', sm: '13px' },
                                                    fontFamily: 'Urbanist',
                                                }}
                                            >
                                                Why would you recommend this person as a Best Boss?
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: '#666666',
                                                    lineHeight: 1.4,
                                                    fontSize: { xs: '12px', sm: '13px' },
                                                    fontFamily: 'Urbanist',
                                                    wordWrap: 'break-word',
                                                }}
                                            >
                                                {typeof recommendation.recommendation === 'string' 
                                                    ? recommendation.recommendation 
                                                    : "Recommedndation not available"}
                                            </Typography>
                                        </Box>

                                    </Box>

                                    {index < recommendations.length - 1 && (
                                        <Box sx={{
                                            height: '1px',
                                            my: 3,
                                            mx: -1
                                        }} />
                                    )}
                                </Box>
                            ))}
                        </CardContent>
                    </Card>
                ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <Typography color="text.secondary">No recommendations found</Typography>
                    </Box>
                )}

                <DeleteModal
                    open={openDeleteModal}
                    onClose={() => setOpenDeleteModal(false)}
                    onDelete={handleDelete}
                    isDeleting={isDeleting}
                    error={error}
                    title="Are You Sure You Want to Delete This Recommendation?"
                    description="This action will permanently remove the recommendation from the boss's profile and cannot be undone."
                    deleteButtonText="Delete Recommendation"
                />
            </Box>
        </Box>
    );
};

export default Recommendations;